import { useState } from 'react'
import { UsageOtherItem } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.types'
import { OtherItem } from './OtherTrayItems.types'

interface SelectedItemWithQuantity {
  item: OtherItem
  quantity: number
}

interface OtherItemSelectorProps {
  otherItems: OtherItem[]
  onAddToUsage: (item: UsageOtherItem) => void
  onClose: () => void
}

export const OtherItemSelectorLogic = ({
  otherItems,
  onAddToUsage,
  onClose,
}: OtherItemSelectorProps) => {
  const [selectedItems, setSelectedItems] = useState<
    Record<string, SelectedItemWithQuantity>
  >({})
  const [selectedStatus, setSelectedStatus] = useState<'implanted' | 'wasted'>(
    'implanted'
  )

  const handleItemSelect = (item: OtherItem) => {
    setSelectedItems((prev) => {
      // Toggle selection
      if (prev[item.device_id]) {
        const newItems = { ...prev }
        delete newItems[item.device_id]
        return newItems
      } else {
        return {
          ...prev,
          [item.device_id]: {
            item,
            quantity: 1,
          },
        }
      }
    })
  }

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStatus(event.target.value as 'implanted' | 'wasted')
  }

  const handleQuantityChange = (
    item: OtherItem,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value, 10)
    if (!isNaN(value)) {
      // Clamp value between 1 and max available
      const max = item.count
      const clamped = Math.min(Math.max(1, value), max)

      setSelectedItems((prev) => ({
        ...prev,
        [item.device_id]: {
          item,
          quantity: clamped,
        },
      }))
    }
  }

  const handleConfirm = () => {
    const itemsToAdd = Object.values(selectedItems)

    if (itemsToAdd.length > 0) {
      // Add all selected items to usage
      itemsToAdd.forEach(({ item, quantity }) => {
        const newItem: UsageOtherItem = {
          count: quantity,
          wasted: selectedStatus === 'wasted',
          deviceId: item.device_id,
          deviceDescription: item.device_description,
        }
        onAddToUsage(newItem)
      })

      onClose()
      // Reset state
      setSelectedItems({})
      setSelectedStatus('implanted')
    }
  }

  const selectedItemsCount = Object.keys(selectedItems).length

  return {
    selectedItems,
    selectedStatus,
    handleItemSelect,
    handleStatusChange,
    handleQuantityChange,
    handleConfirm,
    selectedItemsCount,
  }
}
